// Tier-2 <Tooltip> backed by AntD Tooltip (FORT-331). Colour/animation come from
// AntD's token-sourced theme, so no useTokens() here. AntD requires a single
// element child it can attach a ref to.
import React from 'react';
import { Tooltip as AntTooltip } from 'antd';
import type { CommonProps } from './types';

export type TooltipPlacement =
  | 'top' | 'bottom' | 'left' | 'right'
  | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

export interface TooltipProps extends CommonProps {
  title: React.ReactNode;
  placement?: TooltipPlacement;
  /**
   * What reveals the tooltip. Defaults to hover *and* focus: on an icon-only control the
   * tooltip is the label, and hover-only leaves a sighted keyboard user tabbing onto an
   * unlabelled icon.
   */
  trigger?: 'hover' | 'focus' | 'click' | Array<'hover' | 'focus' | 'click'>;
  /** The element(s) the tooltip is anchored to. */
  children: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  placement = 'top',
  trigger = ['hover', 'focus'],
  children,
  className,
  style,
  'data-testid': dataTestId,
}) => (
  // AntD attaches the tooltip to its child and doesn't forward arbitrary DOM
  // attrs, so wrap the trigger in a span that carries className/style/data-testid
  // (honours CommonProps and gives a stable Playwright selector).
  //
  // The span is `inline-flex` so it hugs its child: as a plain inline box it would carry
  // the inherited line-height strut and end up taller than the control inside it, which
  // makes it the tallest item wherever it sits in a flex row.
  <AntTooltip title={title} placement={placement} trigger={trigger}>
    <span
      className={className}
      style={{ display: 'inline-flex', ...style }}
      data-testid={dataTestId}
    >
      {children}
    </span>
  </AntTooltip>
);
